import { ReactNode } from "react";
import { BaseElementProps, LocalProp, PanelColor } from "../../props";
import { SelectOption } from "../../molecules/Select";
import { TypographyBaseProps, TypographyContent } from "../../atoms/Typography";
import { TextFilterMode } from "./constants";

import { DataTableState } from "./hooks/useDataTable";
import type { SortingState } from "./hooks/useSorting";
import type { FilteringState } from "./hooks/useFiltering";
import type { PaginationState } from "./hooks/usePagination";
import type { SelectionState } from "./hooks/useSelection";
import type { ExpansionState } from "./hooks/useExpansion";
import type { ColumnLayoutState } from "./hooks/useColumnLayout";
import type { PresetsState } from "./hooks/useColumnPresets";
import type { GroupingState, GroupNode } from "./hooks/useGrouping";
import type { FrozenColumnsState } from "./hooks/useFrozenColumns";

// ============ Column Types ============

export enum ColumnType {
  TEXT = "text",
  BOOL = "bool",
  DATE = "date",
  LOCALDATE = "localdate",
  DATETIME = "datetime",
  TIME = "time",
  INTEGER = "integer",
  FLOAT = "float",
  ENUM = "enum",
  LOCALIZED = "localized",
  VIRTUAL = "virtual",
  RENDER = "render",
  INPUT = "input",
}

export type CellTypographyProps = Pick<
  TypographyBaseProps,
  "maxWidth" | "noWrap" | "maxChars" | "showTitle"
>;

export interface DataTableColumn<TRow = any> {
  id: string;
  type: ColumnType;
  align: "left" | "center" | "right";
  sortable: boolean;
  filterable: boolean;
  hidden: boolean;
  label: TypographyContent;
  revealable?: boolean;
  /** Sticks the column to whichever edge its position implies. */
  freeze?: boolean;
  fitContent?: boolean;
  width?: number | string;
  defaultFilterMode?: TextFilterMode;
  group?: boolean;
  groupPath?: (value: any) => any[];
  render?: (row: TRow, column: DataTableColumn<TRow>) => ReactNode;
  renderHead?: (column: DataTableColumn<TRow>) => ReactNode;
  renderGroup?: (node: GroupNode<TRow>) => ReactNode;
  renderNoRows?: () => ReactNode;
  options?: SelectOption[];
  presetLocalKey?: string;
  cellProps?: CellTypographyProps;
}

export type DataTableColumnInput<TRow = any> = Pick<
  DataTableColumn<TRow>,
  "id" | "label"
> &
  Partial<Omit<DataTableColumn<TRow>, "id" | "label">>;

// ============ Row Metadata ============

export interface RowMeta {
  id: string;
  selected?: boolean;
  batchLocked?: boolean;
  isExpanded?: boolean;
}

export type DataTableRow<TRow = any> = TRow & {
  _meta?: RowMeta;
};

// ============ Component Types ============

export interface DataTableProps<TRow> extends BaseElementProps {
  table: DataTableState<TRow>;
  loading?: boolean;
  noRows?: ReactNode;
  renderRowExpansion?: (row: TRow) => ReactNode;
  alwaysExpanded?: boolean;
  rowClasses?: (row: TRow, index: number) => string | string[];
  /** Opaque background class for the whole row. Applied to the row *and* its
   *  frozen cells, so sticky columns stay in step with the rest of the row.
   *  Falls back to the built-in stripe/selection background. */
  rowBackgroundClass?: (row: TRow, index: number) => string | undefined;
  cellClasses?: (column: DataTableColumn<TRow>, row: TRow) => string | string[];
  minHeight?: number;
  fullHeight?: boolean;
  naturalHeight?: boolean;
  enumerations?: Record<string, string[]>;
  localizations?: Record<string, Record<string, string>>;
  localKey?: string;
  localScope?: string[];
  dense?: boolean;
  flush?: boolean;
  color?: PanelColor;
  showToolbar?: boolean;
  defaultFilterExpanded?: boolean;
  filterOnlyToolbar?: boolean;
  renderBatchActions?: (batch: TRow[], clearBatch: () => void) => ReactNode;
  renderFooterActions?: (table: DataTableState<TRow>) => ReactNode;
  renderToolbarActions?: (table: DataTableState<TRow>) => ReactNode;
  onDownload?: () => void;
  virtualScroll?: boolean;
  onLoadMore?: () => void;
  isFetchingMore?: boolean;
  estimatedRowHeight?: number;
}

export interface DataTableContextValue<TRow> {
  dense: boolean;
  loading: boolean;
  color: PanelColor;
  enumerations: Record<string, string[]>;
  localizations: Record<string, Record<string, string>>;
  renderRowExpansion?: (row: TRow) => ReactNode;
  alwaysExpanded?: boolean;
  rowClasses?: (row: TRow, index: number) => string | string[];
  /** Opaque background class for the whole row. Applied to the row *and* its
   *  frozen cells, so sticky columns stay in step with the rest of the row.
   *  Falls back to the built-in stripe/selection background. */
  rowBackgroundClass?: (row: TRow, index: number) => string | undefined;
  cellClasses?: (column: DataTableColumn<TRow>, row: TRow) => string | string[];
  renderBatchActions?: (batch: TRow[], clearBatch: () => void) => ReactNode;
  renderToolbarActions?: (table: DataTableState<TRow>) => ReactNode;
  onDownload?: () => void;
  virtualScroll?: boolean;
  isFetchingMore?: boolean;

  // hook states
  table: DataTableState<TRow>;
  sorting?: SortingState;
  filtering?: FilteringState;
  pagination?: PaginationState;
  selection?: SelectionState<TRow>;
  expansion?: ExpansionState;
  columnLayout?: ColumnLayoutState<TRow>;
  presets?: PresetsState;
  grouping?: GroupingState<TRow>;
  frozen: FrozenColumnsState;
}
